import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  LogOut,
  ChevronDown,
  ChevronRight,
  X,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModuleContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { aurora } from '@/components/aurora/tokens';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { getMasterNavigation, getTenantNavigation, getFilteredNavigation, type NavItem, type NavChild } from '@/lib/navigation';
import type { FreeswitchStatusData, EslHealthData, CountData } from '@easyflow/shared';

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const DRAWER_SPRING = aurora.spring.snappy;
const TENANT_BADGE_FONT = aurora.font.serifTenantBadge;

export default function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const { logout, user } = useAuth();
  const { isModuleEnabled } = useModules();
  const { hasPermission } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const { tenantSlug } = useParams();
  const [messengerUnread, setMessengerUnread] = useState(0);
  const [freeSwitchConnected, setFreeSwitchConnected] = useState<boolean | null>(null);
  const [freeSwitchLoading, setFreeSwitchLoading] = useState(false);
  const [eslConnected, setEslConnected] = useState<boolean | null>(null);
  const [eslLoading, setEslLoading] = useState(false);

  useEffect(() => {
    if (!user || !isOpen || !isModuleEnabled('messenger')) return;
    const fetchUnread = () => {
      apiClient.get<CountData>('/messenger/unread-count')
        .then(d => { if (d.success) setMessengerUnread(d.data?.count || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user, isOpen, isModuleEnabled]);

  const checkFreeSwitchStatus = useCallback(async () => {
    if (!user) {
      setFreeSwitchConnected(null);
      return;
    }
    try {
      setFreeSwitchLoading(true);
      const res = await apiClient.get<FreeswitchStatusData>('/freeswitch/status');
      setFreeSwitchConnected(Boolean(res.data?.connected));
    } catch {
      setFreeSwitchConnected(null);
    } finally {
      setFreeSwitchLoading(false);
    }
  }, [user]);

  const checkEslStatus = useCallback(async () => {
    if (!user) {
      setEslConnected(null);
      return;
    }
    try {
      setEslLoading(true);
      const res = await apiClient.get<EslHealthData>('/system/esl-health');
      setEslConnected(res.data?.state === 'connected');
    } catch {
      setEslConnected(null);
    } finally {
      setEslLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id || !isOpen) return;
    let isMounted = true;
    const run = async () => {
      if (!isMounted) return;
      await checkFreeSwitchStatus();
      await checkEslStatus();
    };
    run();
    const interval = setInterval(run, 60000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user?.id, isOpen, checkFreeSwitchStatus, checkEslStatus]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const rawNavigation = user
    ? (user.is_master_tenant && user.role === 'super_admin')
      ? getMasterNavigation()
      : getTenantNavigation(user.tenantSlug)
    : [];
  // Voci che NON vogliamo mostrare nel menu mobile (richiesta esplicita
  // utente). Restano visibili sul desktop perché il filtro si applica solo
  // in `MobileSidebar` — la sorgente in `lib/navigation.ts` è invariata.
  const HIDDEN_ON_MOBILE = new Set([
    'Workflow Builder',
    'Web Builder',
    'Integrazioni',
    'Logs & Verbose',
  ]);
  // Task #2012 — stessa logica del Sidebar desktop: in modalità POS mostra
  // solo voci POS + Dashboard, niente Web Builder / CRM / AI / ecc.
  const POS_ONLY_NAV_NAMES = new Set<string>([
    'Dashboard', 'Cassa', 'Catalogo', 'Amministrazione', 'Magazzino',
  ]);
  const POS_PATH_SEGMENTS = new Set<string>([
    'cassa', 'catalogo', 'amministrazione', 'punto-cassa',
  ]);
  const pathParts = (typeof window !== 'undefined' ? window.location.pathname : '/').split('/').filter(Boolean);
  const inPosContext = !!pathParts[1] && POS_PATH_SEGMENTS.has(pathParts[1]);
  const navigation = getFilteredNavigation(
    rawNavigation,
    isModuleEnabled,
    hasPermission,
    isAdmin,
  )
    .filter((item) => !HIDDEN_ON_MOBILE.has(item.name))
    .filter((item) => !inPosContext || POS_ONLY_NAV_NAMES.has(item.name));

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const handleNavClick = () => {
    onClose();
  };

  const location = useLocation();
  const navigateMobile = useNavigate();

  const isChildActive = (childHref: string) => {
    if (!childHref.includes('?')) return false;
    const [pathname, search] = childHref.split('?');
    if (location.pathname !== pathname) return false;
    const childParams = new URLSearchParams(search);
    const currentParams = new URLSearchParams(location.search);
    for (const [key, value] of childParams.entries()) {
      if (currentParams.get(key) !== value) return false;
    }
    return true;
  };

  const slug = tenantSlug || user?.tenantSlug || '';

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      const visibleChildren = item.children;
      const isExpanded = expandedItems.includes(item.name);
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleExpanded(item.name)}
            className={cn(
              "group flex items-center w-full px-3 py-3 min-h-[44px] text-base font-medium rounded-lg",
              "text-gray-700 dark:text-gray-300",
              "hover:bg-blue-50 dark:hover:bg-blue-900/30",
              "transition-all duration-200"
            )}
          >
            <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="flex-1 text-left">{item.name}</span>
            {item.moduleKey === 'messenger' && messengerUnread > 0 && (
              <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center rounded-full mr-2">
                {messengerUnread > 99 ? '99+' : messengerUnread}
              </Badge>
            )}
            {isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </button>
          {isExpanded && (
            <div className="ml-6 mt-1 space-y-0.5">
              {visibleChildren.map((child: NavChild) => {
                if (child.newWindow) {
                  return (
                    <button
                      key={child.name}
                      onClick={() => { handleNavClick(); const w = 1024, h = 720, left = (screen.width - w) / 2, top = (screen.height - h) / 2; window.open(child.href, 'pos-window', `popup=yes,location=no,toolbar=no,menubar=no,status=no,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`); }}
                      className={cn(
                        "group flex items-center w-full px-3 py-3 min-h-[44px] text-sm font-medium rounded-lg",
                        "transition-all duration-200",
                        'text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      )}
                    >
                      <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                      {child.name}
                      <ExternalLink className="ml-auto h-3 w-3 opacity-40" />
                    </button>
                  );
                }
                const hasQueryParams = child.href.includes('?');
                const isMessengerChild = item.moduleKey === 'messenger' && (
                  child.name === 'Chat' || child.name === 'Canali' || child.name === 'Gruppi di Lavoro'
                );
                if (hasQueryParams) {
                  const active = isChildActive(child.href);
                  return (
                    <button
                      key={child.name}
                      onClick={() => { handleNavClick(); navigateMobile(child.href); }}
                      className={cn(
                        "group flex items-center w-full px-3 py-3 min-h-[44px] text-sm font-medium rounded-lg",
                        "transition-all duration-200",
                        active
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                      )}
                    >
                      <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 text-left">{child.name}</span>
                      {isMessengerChild && messengerUnread > 0 && (
                        <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center rounded-full">
                          {messengerUnread > 99 ? '99+' : messengerUnread}
                        </Badge>
                      )}
                    </button>
                  );
                }
                return (
                <NavLink
                  key={child.name}
                  to={child.href}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center px-3 py-3 min-h-[44px] text-sm font-medium rounded-lg",
                      "transition-all duration-200",
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    )
                  }
                >
                  <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  <span className="flex-1">{child.name}</span>
                  {isMessengerChild && messengerUnread > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-5 min-w-[20px] justify-center rounded-full">
                      {messengerUnread > 99 ? '99+' : messengerUnread}
                    </Badge>
                  )}
                </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Task #3585 — href assoluti (console master su origin dedicato):
    // navigazione full-page cross-origin, NavLink non è applicabile.
    if (item.href && /^https?:\/\//.test(item.href)) {
      return (
        <a
          key={item.name}
          href={item.href}
          onClick={handleNavClick}
          className={cn(
            "group flex items-center px-3 py-3 min-h-[44px] text-base font-medium rounded-lg",
            "transition-all duration-200",
            'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30'
          )}
        >
          <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
          <span>{item.name}</span>
        </a>
      );
    }

    return (
      <NavLink
        key={item.name}
        to={item.href!}
        end={item.exact}
        onClick={handleNavClick}
        className={({ isActive }) =>
          cn(
            "group flex items-center px-3 py-3 min-h-[44px] text-base font-medium rounded-lg",
            "transition-all duration-200",
            isActive
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
              : 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/30'
          )
        }
      >
        <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
        <span>{item.name}</span>
      </NavLink>
    );
  };

  const tenantBadgeSlug = user?.tenantSlug || slug;
  const roleLabel = user?.role === 'admin'
    ? 'Admin'
    : user?.role === 'super_admin'
      ? 'Super Admin'
      : 'User';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="mobile-sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={onClose}
          />

          <motion.div
            key="mobile-sidebar-drawer"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={DRAWER_SPRING}
            className={cn(
              "fixed inset-y-0 left-0 w-80 max-w-[85vw] z-50 md:hidden",
              "bg-white dark:bg-slate-900 shadow-2xl"
            )}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 gap-3">
                <div className="flex items-center min-w-0 gap-2">
                  <img src="/logos/easyflow-full-v3.svg" alt="EasyFlow" className="h-9 flex-shrink-0" />
                  {tenantBadgeSlug && (
                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-md truncate max-w-[110px] leading-tight" title={`${tenantBadgeSlug} · ${roleLabel}`}>
                      <span className={`${TENANT_BADGE_FONT} text-gray-700 dark:text-gray-200`}>{tenantBadgeSlug}</span>
                      <span className="opacity-60"> · {roleLabel}</span>
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2 flex-shrink-0"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                <nav className="space-y-0.5">
                  {navigation.map(renderNavItem)}
                </nav>

                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">Sistema</p>
                  <div className="space-y-1.5">
                    <button
                      type="button"
                      onClick={checkFreeSwitchStatus}
                      disabled={freeSwitchLoading}
                      className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-800/60 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={cn(
                          "h-2 w-2 rounded-full flex-shrink-0",
                          freeSwitchConnected === true && 'bg-green-500 animate-pulse',
                          freeSwitchConnected === false && 'bg-red-500',
                          freeSwitchConnected === null && 'bg-gray-400'
                        )} />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">FreeSWITCH</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={cn(
                          "text-[11px] font-medium",
                          freeSwitchConnected === true && 'text-green-600 dark:text-green-400',
                          freeSwitchConnected === false && 'text-red-600 dark:text-red-400',
                          freeSwitchConnected === null && 'text-gray-400'
                        )}>
                          {freeSwitchConnected === true ? 'Online' : freeSwitchConnected === false ? 'Offline' : '—'}
                        </span>
                        <RefreshCw className={cn("h-3 w-3 text-gray-400", freeSwitchLoading && 'animate-spin')} />
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={checkEslStatus}
                      disabled={eslLoading}
                      className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-800/60 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={cn(
                          "h-2 w-2 rounded-full flex-shrink-0",
                          eslConnected === true && 'bg-green-500 animate-pulse',
                          eslConnected === false && 'bg-red-500',
                          eslConnected === null && 'bg-yellow-500'
                        )} />
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">ESL</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={cn(
                          "text-[11px] font-medium",
                          eslConnected === true && 'text-green-600 dark:text-green-400',
                          eslConnected === false && 'text-red-600 dark:text-red-400',
                          eslConnected === null && 'text-yellow-600 dark:text-yellow-400'
                        )}>
                          {eslConnected === true ? 'Online' : eslConnected === false ? 'Offline' : 'N/A'}
                        </span>
                        <RefreshCw className={cn("h-3 w-3 text-gray-400", eslLoading && 'animate-spin')} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                      <span className="text-sm font-medium text-white">
                        {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.role === 'admin' ? 'Tenant Admin' : user?.role === 'super_admin' ? 'Super Admin' : 'User'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => { await logout(); onClose(); }}
                  className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Esci</span>
                </Button>
                <div className="text-center pt-2 border-t border-gray-200/50 dark:border-gray-700/50">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    Crafted by{' '}
                    <a
                      href="https://easydigitalgroup.it"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline transition-colors inline-flex items-center gap-0.5"
                    >
                      Easydigitalgroup
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
